import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Spinner } from "react-bootstrap";
import Card from "../../../components/Card";
import { Modal, Button } from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import productImage from "../../../assets/images/store/product.jpeg";
import axios from "axios";
import { useProjectName } from "../../../Context/ProjectNameContext";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import { getToken } from "../../../utils/auth";
import Cookies from 'js-cookie';

const StoreDetail = () => {
  const navigate = useNavigate();
  const defaultCurrency = "USD";
  const defaultSymbol = "$";
  const [products, setProducts] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showAllQrcModal, setShowAllQrcModal] = useState(false);
  const handleShowPopup = () => setShowPopup(true);
  const handleClosePopup = () => setShowPopup(false);
  const [loading, setLoading] = useState(false);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [qrCodeRequest, setQrCodeRequest] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [qrcData, setQrcData] = useState([]);
  const [filter, setFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loadingFull, setLoadingFull] = useState(false);
  const { t, i18n } = useTranslation();
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(
    localStorage.getItem("selectedCurrency") || defaultCurrency
  );
  const [currencySymbol, setCurrencySymbol] = useState(
    localStorage.getItem("currencySymbol") || defaultSymbol
  );
  const [user, setUser] = useState("");
  
  // New states for scroll pagination
  const [productPage, setProductPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const observerTarget = useRef(null);

  const fetchUserData = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/user`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUser(response?.data?.data);
    } catch (error) {
      console.error("Failed Fetching Details", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleShowAllQrc = () => setShowAllQrcModal(true);
  const handleCloseAllQrc = () => setShowAllQrcModal(false);

  const fetchProducts = async (page = 1, append = false) => {
    if (page === 1) {
      setLoadingP(true);
    } else {
      setLoadingMore(true);
    }

    const url = `${process.env.REACT_APP_URL_ECOMMERCE}/wp-json/wc/v3/products?page=${page}&per_page=20&wmc-currency=${selectedCurrency}`;
    const authHeader = "Basic YWRtaW46aTVJTyA5aEZyIGFIU00gOUtVVCBuc2pYIFFOb3A=";

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: authHeader,
        },
      });

      // Filter out draft products
      const publishedProducts = response.data.filter(
        (product) => product.status === "publish"
      );

      if (page === 1) {
        const categories = [
          ...new Set(publishedProducts?.map((p) => p.categories[0]?.name)),
        ];
        setCategories(categories?.filter(Boolean));
        setAllProducts(publishedProducts);
        setProducts(publishedProducts);
        setFilteredProducts(publishedProducts);
      } else if (append) {
        setAllProducts((prev) => [...prev, ...publishedProducts]);
        setProducts((prev) => [...prev, ...publishedProducts]);
        setFilteredProducts((prev) => [...prev, ...publishedProducts]);
      }

      // Check if there are more products
      if (publishedProducts.length < 20) {
        setHasMoreProducts(false);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setHasMoreProducts(false);
    } finally {
      setLoadingP(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setProductPage(1);
    setHasMoreProducts(true);
    fetchProducts(1, false);
  }, [selectedCurrency]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreProducts && !loadingMore && !loadingP) {
          setProductPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMoreProducts, loadingMore, loadingP]);

  useEffect(() => {
    if (productPage > 1) {
      fetchProducts(productPage, true);
    }
  }, [productPage]);

  useEffect(() => {
    const handleStorageChange = () => {
      const newCurrency = localStorage.getItem("selectedCurrency");
      const newSymbol = localStorage.getItem("currencySymbol");
      if (newCurrency && newCurrency !== selectedCurrency) {
        setSelectedCurrency(newCurrency);
      }
      if (newSymbol && newSymbol !== currencySymbol) {
        setCurrencySymbol(newSymbol);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [selectedCurrency, currencySymbol]);

  useEffect(() => {
    const savedCurrency = localStorage.getItem("selectedCurrency");
    if (user?.isCookieConsent) {
      Cookies.set("currency", savedCurrency, { expires: 30 });
    }
    if (!localStorage.getItem("selectedCurrency")) {
      localStorage.setItem("selectedCurrency", defaultCurrency);
      if (user?.isCookieConsent) {
        Cookies.set("currency", defaultCurrency, { expires: 30 });
      }
    }
    if (!localStorage.getItem("currencySymbol")) {
      localStorage.setItem("currencySymbol", defaultSymbol);
    }

    const fetchCurrencies = async () => {
      const url = `${process.env.REACT_APP_URL_ECOMMERCE}/wp-json/wc/v3/data/currencies`;
      const authHeader = "Basic YWRtaW46aTVJTyA5aEZyIGFIU00gOUtVVCBuc2pYIFFOb3A=";

      try {
        setLoadingFull(true);

        const response = await axios.get(url, {
          headers: {
            Authorization: authHeader,
          },
        });
        setCurrencies(response.data);
        const savedCurrency = localStorage.getItem("selectedCurrency");
        const savedCurrencyData = response.data.find(
          (currency) => currency.code === savedCurrency
        );

        if (savedCurrencyData) {
          setCurrencySymbol(decodeHtmlEntity(savedCurrencyData.symbol));
        } else {
          setCurrencySymbol(defaultSymbol);
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
      } finally {
        setLoadingFull(false);
      }
    };

    fetchCurrencies();
  }, []);

  const cookiesPut = async (selectedCode) => {
    try {
      const token = getToken();
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/api/user/cookies`,
        { currency: selectedCode },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      console.log("Failed fetching connection", error);
    }
  };

  const handleCurrencyChange = (event) => {
    const selectedCode = event.target.value;
    setSelectedCurrency(selectedCode);

    localStorage.setItem("selectedCurrency", selectedCode);
    if (user?.isCookieConsent) {
      Cookies.set("currency", selectedCode, { expires: 30 });
      cookiesPut(selectedCode);
    }
    const selectedCurrencyData = currencies?.find(
      (currency) => currency.code === selectedCode
    );
    if (selectedCurrencyData) {
      const symbol = decodeHtmlEntity(selectedCurrencyData.symbol);
      setCurrencySymbol(symbol);
      localStorage.setItem("currencySymbol", symbol);
    }
  };

  const decodeHtmlEntity = (str) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = str;
    return textarea.value;
  };

  const handleNavigate = (id) => {
    navigate(`/store-detail/${id}`, {
      state: { selectedCurrency, currencySymbol },
    });
  };

  const handleQrCodeRequest = (e) => {
    setQrCodeRequest(e.target.value);
  };

  const submitQrCodeRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/api/qrc/identifier-reservation/request`,
        {
          identifier: qrCodeRequest,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success(
        response?.data?.message || "QR Code request sent successfully!"
      );
      setQrCodeRequest("");
      handleClosePopup();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to send QR Code request."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGetQrc = async (page = 1, limit = 20) => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/qrc/admin/special/all?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setQrcData(response?.data?.data?.qrcList);
      setTotalPages(Math.ceil(response?.data?.data?.noOfEntries / limit));
    } catch (error) {
      console.log("Failed Fetching qrc", error);
    }
  };

  useEffect(() => {
    if (showAllQrcModal) {
      handleGetQrc(currentPage, pageSize);
    }
  }, [showAllQrcModal, currentPage]);

  const filteredQrcData = qrcData.filter((qrc) =>
    qrc.qrc_UID.toLowerCase().includes(filter.toLowerCase())
  );

  const applyFilter = () => {
    const filtered = allProducts.filter((product) => {
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const withinCategory = selectedCategory
        ? product.categories.some((c) => c.name === selectedCategory)
        : true;
      return matchesSearch && withinCategory;
    });
    setFilteredProducts(filtered);
  };

  const clearFilter = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setFilteredProducts(allProducts);
  };

  const isFilterApplied = searchQuery || selectedCategory;
  const handlenavigate = () => {
    navigate("/request-qrc");
  };
  const projectName = useProjectName();

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      {loadingFull && (
        <div style={styles.loaderOverlay}>
          <CircularProgress style={{ color: "white" }} />
        </div>
      )}

      <div id="content-page" className="content-page">
        <Container>
          <Row>
            <Col lg="12">
              <Card className="shadow-none p-0">
                <Card.Header
                  className="rounded border-bottom-0"
                  style={{
                    padding: "15px 20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "15px",
                      width: "100%",
                    }}
                  >
                    <div className="header-title" style={{ flex: "1 1 auto", minWidth: "200px" }}>
                      <h4
                        style={{
                          margin: "0",
                          fontSize: "clamp(1rem, 2.5vw, 1.25rem)",
                          fontWeight: "600",
                          color: "#1F2937",
                        }}
                      >
                        {t("welcome_to")} {projectName} {t("store")}
                      </h4>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                        flex: "0 1 auto",
                      }}
                    >
                      <select
                        value={selectedCurrency}
                        onChange={handleCurrencyChange}
                        className="form-select"
                        style={{
                          width: "auto",
                          minWidth: "150px",
                          maxWidth: "160px",
                          fontSize: "0.875rem",
                          padding: "6px 12px",
                        }}
                      >
                        <option value="" disabled>
                          Select Currency
                        </option>
                        {currencies?.map((currency) => (
                          <option key={currency?.code} value={currency?.code}>
                            {currency?.name} ({decodeHtmlEntity(currency?.symbol)})
                          </option>
                        ))}
                      </select>

                      <Button
                        variant="primary"
                        onClick={handlenavigate}
                        style={{
                          fontSize: "0.875rem",
                          padding: "6px 16px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("request_qr_code")}
                      </Button>
                    </div>
                  </div>

                  <style>
                    {`
                      @media (max-width: 576px) {
                        .header-title h4 {
                          font-size: 0.95rem !important;
                        }
                      }
                      
                      @media (max-width: 480px) {
                        .form-select {
                          min-width: 100px !important;
                          font-size: 0.8rem !important;
                        }
                        
                        .btn {
                          font-size: 0.8rem !important;
                          padding: 5px 12px !important;
                        }
                      }
                    `}
                  </style>
                </Card.Header>
              </Card>

              <Modal show={showPopup} onHide={handleClosePopup} centered>
                <div
                  className=""
                  style={{ marginBlock: "5px", cursor: "pointer" }}
                >
                  <div className="">
                    <Modal.Header closeButton>
                      <Modal.Title>{t("requested_qrc")} </Modal.Title>
                    </Modal.Header>
                    <div className="">
                      <Card>
                        <Card.Body>
                          <Form onSubmit={submitQrCodeRequest}>
                            <Form.Group className="form-group">
                              <Form.Label
                                htmlFor="qrCodeRequest"
                                className="form-label"
                              >
                                {t("enter_qr_code_id")}{" "}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                className="form-control"
                                id="qrCodeRequest"
                                name="qrCodeRequest"
                                placeholder={t("enter_qr_code_id")}
                                value={qrCodeRequest}
                                onChange={handleQrCodeRequest}
                                required
                              />
                            </Form.Group>
                            <Button
                              type="submit"
                              className="btn btn-primary me-2"
                            >
                              {loading
                                ? t("submitting")
                                : t("submit_request_button")}
                            </Button>
                          </Form>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </div>
              </Modal>

              <Form
                className="my-3"
                style={{
                  background: "#fff",
                  padding: "10px",
                  borderRadius: "5px",
                }}
              >
                <Row>
                  <Col md={4} style={{ marginBottom: "10px" }}>
                    <Form.Group>
                      <Form.Label>{t("search_product")}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t("enter_product_name")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} style={{ marginBottom: "10px" }}>
                    <Form.Group>
                      <Form.Label>{t("category")}</Form.Label>
                      <Form.Control
                        as="select"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        <option value="">{t("all_categories")}</option>
                        {categories.map((category, idx) => (
                          <option key={idx} value={category}>
                            {category}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col
                    md={4}
                    className="d-flex align-items-end"
                    style={{ marginBottom: "10px" }}
                  >
                    {isFilterApplied ? (
                      <>
                        <Button
                          variant="primary"
                          onClick={applyFilter}
                          style={{ width: "100%" }}
                        >
                          {t("apply_filter")}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={clearFilter}
                          style={{ width: "100%", marginLeft: "5px" }}
                        >
                          {t("clear_filter")}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={applyFilter}
                        style={{ width: "100%" }}
                      >
                        {t("apply_filter")}
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>

              {loadingP ? (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "30px",
                    marginBottom: "30px",
                  }}
                >
                  <Spinner
                    animation="border"
                    role="status"
                    style={{
                      width: "2rem",
                      height: "2rem",
                      color: "#4F46E5",
                    }}
                  >
                    <span className="visually-hidden">{t("query_loading")}</span>
                  </Spinner>
                  <p
                    style={{
                      marginTop: "10px",
                      fontSize: "0.85rem",
                      color: "#6B7280",
                    }}
                  >
                    {t("loading_products")}
                  </p>
                </div>
              ) : (
                <>
                  <div className="row g-2 g-md-3">
                    {filteredProducts.length > 0 ? (
                      filteredProducts?.map((data, index) => (
                        <>
                          {(data?.stock_quantity > 0 || data?.stock_quantity === null) && (
                            <Col xs="6" sm="6" md="4" lg="3" key={index}>
                              <Card
                                className="card-block card-stretch card-height product h-100"
                                style={{
                                  transition: "all 0.25s ease",
                                  cursor: "pointer",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  border: "1px solid #e5e7eb",
                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                  background: "#ffffff",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = "translateY(-3px)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 12px rgba(0, 0, 0, 0.1)";
                                  e.currentTarget.style.borderColor = "#4F46E5";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "translateY(0)";
                                  e.currentTarget.style.boxShadow =
                                    "0 1px 2px rgba(0, 0, 0, 0.05)";
                                  e.currentTarget.style.borderColor = "#e5e7eb";
                                }}
                              >
                                <Card.Body style={{ padding: "0" }}>
                                  <div
                                    onClick={() => handleNavigate(data?.id)}
                                    className="image-block position-relative"
                                    style={{
                                      width: "100%",
                                      paddingTop: "100%",
                                      cursor: "pointer",
                                      overflow: "hidden",
                                      background: "#f9fafb",
                                      position: "relative",
                                    }}
                                  >
                                    {data?.qrc_data?.qrc_type === "special" ? (
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "0",
                                          left: "0",
                                          width: "100%",
                                          height: "100%",
                                          display: "flex",
                                          flexDirection: "column",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          background:
                                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                          padding: "10px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "0.75rem",
                                            fontWeight: "700",
                                            marginBottom: "4px",
                                            color: "#ffffff",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.3px",
                                          }}
                                        >
                                          {t("special_qrc")}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "0.7rem",
                                            fontWeight: "500",
                                            color: "#f0f0f0",
                                            textAlign: "center",
                                          }}
                                        >
                                          {data?.qrc_data?.qrc_title}
                                        </div>
                                      </div>
                                    ) : (
                                      <img
                                        src={data?.images?.[0]?.src || productImage}
                                        style={{
                                          position: "absolute",
                                          top: "0",
                                          left: "0",
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                          transition: "transform 0.3s ease",
                                        }}
                                        className="img-fluid product-image"
                                        alt="product-img"
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.transform = "scale(1.05)";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.transform = "scale(1)";
                                        }}
                                      />
                                    )}
                                  </div>

                                  <div style={{ padding: "8px" }}>
                                    <h5
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "0.8rem",
                                        color: "#1F2937",
                                        marginBottom: "3px",
                                        transition: "color 0.2s ease",
                                        cursor: "pointer",
                                        lineHeight: "1.2",
                                        height: "29px",
                                        display: "-webkit-box",
                                        WebkitLineClamp: "2",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color = "#4F46E5";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = "#1F2937";
                                      }}
                                      onClick={() => handleNavigate(data?.id)}
                                    >
                                      {data?.name}
                                    </h5>

                                    {data?.short_description && (
                                      <div
                                        style={{
                                          color: "#6B7280",
                                          fontSize: "0.65rem",
                                          lineHeight: "1.2",
                                          height: "24px",
                                          marginBottom: "5px",
                                          overflow: "hidden",
                                          display: "-webkit-box",
                                          WebkitLineClamp: "2",
                                          WebkitBoxOrient: "vertical",
                                        }}
                                      >
                                        {data.short_description.length > 35 ? (
                                          <span
                                            dangerouslySetInnerHTML={{
                                              __html:
                                                data.short_description.substring(0, 35) +
                                                "...",
                                            }}
                                          />
                                        ) : (
                                          <span
                                            dangerouslySetInnerHTML={{
                                              __html: data.short_description,
                                            }}
                                          />
                                        )}
                                      </div>
                                    )}

                                    <div
                                      style={{
                                        borderTop: "1px solid #F3F4F6",
                                        paddingTop: "6px",
                                        marginTop: "5px",
                                      }}
                                    >
                                      <div
                                        className="d-flex align-items-center justify-content-between"
                                        style={{ gap: "3px" }}
                                      >
                                        <div className="d-flex flex-column">
                                          <span
                                            style={{
                                              fontSize: "0.95rem",
                                              fontWeight: "700",
                                              color: "#1F2937",
                                              lineHeight: "1.1",
                                            }}
                                          >
                                            {currencySymbol}
                                            {(parseFloat(data?.sale_price) || 0).toFixed(2)}
                                          </span>
                                          {data?.regular_price && data?.sale_price && (
                                            <span
                                              style={{
                                                fontSize: "0.65rem",
                                                textDecoration: "line-through",
                                                color: "#9CA3AF",
                                                lineHeight: "1.1",
                                              }}
                                            >
                                              {currencySymbol}
                                              {(parseFloat(data?.regular_price) || 0).toFixed(
                                                2
                                              )}
                                            </span>
                                          )}
                                        </div>
                                        {data?.regular_price && data?.sale_price && (
                                          <span
                                            style={{
                                              fontSize: "0.65rem",
                                              color: "#ffffff",
                                              fontWeight: "600",
                                              background: "#10b981",
                                              padding: "2px 6px",
                                              borderRadius: "8px",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {Math.round(
                                              ((data.regular_price - data.sale_price) /
                                                data.regular_price) *
                                                100
                                            )}
                                            % OFF
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          )}
                        </>
                      ))
                    ) : (
                      <div className="col-12">
                        <div
                          style={{
                            textAlign: "center",
                            padding: "30px 15px",
                            background: "#f9fafb",
                            borderRadius: "8px",
                            margin: "10px 0",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "2rem",
                              marginBottom: "8px",
                              opacity: "0.3",
                            }}
                          >
                            📦
                          </div>
                          <p
                            style={{
                              fontSize: "0.9rem",
                              color: "#6B7280",
                              fontWeight: "500",
                              marginBottom: "0",
                            }}
                          >
                            {t("no_products_found")}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Loading More Indicator */}
                  {loadingMore && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ color: "#4F46E5" }}
                      />
                      <p style={{ fontSize: "0.85rem", color: "#6B7280", marginTop: "8px" }}>
                        Loading more products...
                      </p>
                    </div>
                  )}

                  {/* Intersection Observer Target */}
                  <div ref={observerTarget} style={{ height: "20px" }} />

                  {/* End of Products Message */}
                  {!hasMoreProducts && filteredProducts.length > 0 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#6B7280",
                        fontSize: "0.85rem",
                      }}
                    >
                      No more products to load
                    </div>
                  )}
                </>
              )}

              <Modal show={showAllQrcModal} onHide={handleCloseAllQrc} centered>
                <Modal.Header closeButton>
                  <Modal.Title>{t("available_qr_codes")}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "55vh", overflowY: "auto" }}>
                  <Form>
                    <Form.Group controlId="filterQrc">
                      <Form.Label>{t("filter_by_name")}:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t("enter_qr_code_name")}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                      />
                    </Form.Group>
                  </Form>

                  <div className="mt-3">
                    {filteredQrcData.length ? (
                      filteredQrcData.map((qrc) => (
                        <div
                          key={qrc._id}
                          className="p-3 mb-2 border rounded shadow-sm"
                        >
                          <h5 className="fw-bold">
                            {t("qr_code")}: {qrc.qrc_UID}
                          </h5>
                          <div>
                            {t("generated_from")}: {qrc.generatedFrom}
                          </div>
                          <div>
                            {t("price")}: ${qrc.price}
                          </div>
                          <Button
                            variant="primary"
                            style={{ marginTop: "10px" }}
                          >
                            {t("purchase")}
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted">{t("no_qr_codes_match")}.</p>
                    )}
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="primary" onClick={handleCloseAllQrc}>
                    {t("close")}
                  </Button>
                </Modal.Footer>
              </Modal>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

const styles = {
  loaderOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
};

export default StoreDetail;